import * as React from "react";
import "./styles.scss";
import { useContext, useEffect, useState } from "react";
import { Button, Pagination, Table } from "semantic-ui-react";
import PlayerCardRow from "./PlayerCardRow";
import PlayerCardTab from "../PlayerCardTab";
import AddPlayerCardModal from "../../AddPlayerCardModal";
import { PlayerCardSettingsContext } from "../../Contexts";
import { PlayerCard, PlayerCardSortColumn } from "../../../../services/bedrock";
import PlayerCardFilters from "./PlayerCardFilters";
import SendNotificationConfirmation from "../../SendNotificationConfirmation";

interface PlayerCardsTabProps {
  active: boolean;
  onViewMetrics?: (playerCardWidgetId: number) => void;
}

const PlayerCardsTab: React.FC<PlayerCardsTabProps> = ({ active, onViewMetrics }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [playerCardForGrantingAccess, setPlayerCardForGrantingAccess] = useState<PlayerCard | undefined>(undefined);

  const { Preview, PlayerCards, User } = useContext(PlayerCardSettingsContext);

  useEffect(() => {
    if (active && typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get("create") === "true") {
        setIsAddModalOpen(true);
      }
    }
  }, [active]);

  useEffect(() => {
    if (!active || PlayerCards.inEditMode) return;
    if (PlayerCards.selectedPlayerCard) {
      Preview.setPlayerCard(PlayerCards.selectedPlayerCard);
    } else {
      Preview.setPlayerCard(undefined);
    }
  }, [active, PlayerCards.inEditMode, PlayerCards.selectedPlayerCard]);

  useEffect(() => {
    if (PlayerCards.inEditMode) return;
    if (
      !PlayerCards.selectedPlayerCard ||
      !PlayerCards.playerCards.find(pc => pc.id === PlayerCards.selectedPlayerCard!.id)
    ) {
      PlayerCards.setSelectedPlayerCard(PlayerCards.playerCards[0]);
    }
  }, [PlayerCards.playerCards, PlayerCards.inEditMode, PlayerCards.selectedPlayerCard]);

  if (PlayerCards.inEditMode && PlayerCards.selectedPlayerCard) {
    return (
      <PlayerCardTab
        active={active}
        playerCard={PlayerCards.selectedPlayerCard}
        onViewMetrics={onViewMetrics}
        onPlayerCardSaved={playerCard => {
          PlayerCards.setSelectedPlayerCard(playerCard as PlayerCard);
          PlayerCards.loadPage();
        }}
        onClose={() => PlayerCards.setInEditMode(false)}
      />
    );
  }

  const getSortedValueForColumn = (sortColumn: PlayerCardSortColumn) => {
    if (PlayerCards.sortColumn === sortColumn) {
      return PlayerCards.sortOrder === "asc" ? "ascending" : "descending";
    } else {
      return undefined;
    }
  };

  const handleEnablePlayerCardAccess = (playerCard: PlayerCard) => {
    setPlayerCardForGrantingAccess(playerCard);
  };

  const handleSendNotificationConfirmationClosed = (sendNotification: boolean | undefined) => {
    var playerCard: PlayerCard | undefined;
    setPlayerCardForGrantingAccess(prev => {
      playerCard = prev;
      return undefined;
    });
    if (sendNotification !== undefined && playerCard !== undefined) {
      PlayerCards.grantPlayerCardAccess(playerCard, sendNotification);
    }
  };

  return (
    <div className={`playerCards ${active ? "active" : ""}`}>
      <AddPlayerCardModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />
      <SendNotificationConfirmation
        open={!!playerCardForGrantingAccess}
        playerName={playerCardForGrantingAccess?.player?.fullName || "Player"}
        onClose={handleSendNotificationConfirmationClosed}
      />
      <div className="playerCardsTableContainer">
        <PlayerCardFilters />
        <Table
          className={`playerCardsTable ${PlayerCards.isLoading && PlayerCards.playerCards.length > 0 ? "loading" : ""}`}
          selectable
          celled
          sortable
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={getSortedValueForColumn("player")}
                onClick={() => PlayerCards.setSort("player")}
                width="4"
              >
                Player
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSortedValueForColumn("template")}
                onClick={() => PlayerCards.setSort("template")}
                width="4"
              >
                Template
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSortedValueForColumn("theme")}
                onClick={() => PlayerCards.setSort("theme")}
                width="4"
              >
                Theme
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSortedValueForColumn("views")}
                onClick={() => PlayerCards.setSort("views")}
                width="1"
              >
                Views
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSortedValueForColumn("clicks")}
                onClick={() => PlayerCards.setSort("clicks")}
                width="1"
              >
                Clicks
              </Table.HeaderCell>
              <Table.HeaderCell width="1" />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {PlayerCards.isLoading && PlayerCards.playerCards.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">
                  Loading...
                </Table.Cell>
              </Table.Row>
            )}
            {!PlayerCards.isLoading && PlayerCards.playerCards.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="6" textAlign="center">
                  There are no Player Cards.
                </Table.Cell>
              </Table.Row>
            )}
            {PlayerCards.playerCards.map(playerCard => (
              <PlayerCardRow
                key={playerCard.id}
                playerCard={playerCard}
                onEnablePlayerCardAccess={handleEnablePlayerCardAccess}
              />
            ))}
          </Table.Body>
          {User.hasWritePermission && (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="6">
                  <Button primary content="Add Player Card" onClick={() => setIsAddModalOpen(true)} />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
        {PlayerCards.totalPages > 1 && (
          <Pagination
            secondary
            pointing
            activePage={PlayerCards.activePage}
            totalPages={PlayerCards.totalPages}
            firstItem={null}
            lastItem={null}
            onPageChange={(_event, data) => PlayerCards.loadPage(Number(data.activePage))}
          />
        )}
      </div>
    </div>
  );
};
export default PlayerCardsTab;
